// frontend/src/pages/Dashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import HomeBox from "../components/HomeBox";
import UserInfo from "../components/UserInfo";
import logo from "../assets/dzr-logo.png";

const API_URL = process.env.REACT_APP_API_URL || "";
const HQ = process.env.REACT_APP_HQ_LABEL || "Sede";

/* ---------- helpers UI ---------- */
const apiFetch = async (url, opts = {}, timeoutMs = 8000) => {
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(url, { ...opts, signal: ctrl.signal });
    return res;
  } finally {
    clearTimeout(t);
  }
};

const fmtMoney = (v) => {
  const n = Number(v || 0);
  return n.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const lastMonths = () => {
  const out = [];
  const today = new Date();
  for (let i = 0; i < 12; i++) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    out.push({
      value: d.toISOString().slice(0, 7), // YYYY-MM
      label: d.toLocaleDateString(undefined, { year: "numeric", month: "long" }),
    });
  }
  return out;
};

const downloadBlob = (url, filename, token) => {
  apiFetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((r) => r.blob())
    .then((blob) => {
      const a = document.createElement("a");
      const objUrl = window.URL.createObjectURL(blob);
      a.href = objUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(objUrl);
    });
};
export default function Dashboard() {
  const nav = useNavigate();

  const [role, setRole] = useState(localStorage.getItem("me.role") || "");
  const [meData, setMeData] = useState(null);
  const [appWallet, setAppWallet] = useState(null);
  const [appLatestOrder, setAppLatestOrder] = useState(null);
  const [appNotifications, setAppNotifications] = useState([]);
    const [appSponsors, setAppSponsors] = useState([]);
    const [activeSponsorIndex, setActiveSponsorIndex] = useState(0);
  const [kpiData, setKpiData] = useState(null);
  const [securitySummary, setSecuritySummary] = useState(null);
  const [hasFullPackage, setHasFullPackage] = useState(false);

  const isMadre = role === "MADRE";
  const isAziendaUI = role === "AZIENDA";
  const isAgente = role === "AGENTE";
  const isFranchising = role === "FRANCHISING";
  const isPV = role === "PV";
  const isAzienda = role === "AZIENDA";

  // KPI / dati
  const [allClients, setAllClients] = useState([]);
const [activeClientsCount, setActiveClientsCount] = useState(null);
  const [productsCount, setProductsCount] = useState(0);
  const [pendingOrders, setPendingOrders] = useState(0);

  // KPI store (solo MADRE): supportiamo sia dropdown (se arriva lista) sia input manuale
  const [stores, setStores] = useState([]);
  const [selectedStore, setSelectedStore] = useState("");

  // 📥 INBOX PROFORMA (solo ruoli ≠ MADRE)
  const [inboxMonth, setInboxMonth] = useState(lastMonths()[0].value);
  const [inboxEnabled, setInboxEnabled] = useState(false);
  const [inboxCount, setInboxCount] = useState(0);
  const [inboxUnpaid, setInboxUnpaid] = useState(0);
  const [inboxSum, setInboxSum] = useState("0.00");
  const [inboxLast, setInboxLast] = useState([]);
  const [globalUnpaid, setGlobalUnpaid] = useState({ count: 0, total: 0 });
  const [receivedUnpaid, setReceivedUnpaid] = useState(0);

  const activeClients = useMemo(() => {
  if (typeof activeClientsCount === "number") return activeClientsCount;

  return (allClients || []).filter((c) => c?.is_active !== false).length;
}, [allClients, activeClientsCount]);
const handleLogout = () => {
    localStorage.clear();
    window.location.hash = "#/login";
  };

  const token = localStorage.getItem("access") || "";
  const headers = useMemo(() => {
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [token]);

  useEffect(() => {
    if (!token || (role || "").toUpperCase() !== "CLIENTE") return;

    const loadLatestOrder = async () => {
      try {
        const r = await fetch(`${API_URL}/api/app/ordini/storico/`, { headers });
        const data = r.ok ? await r.json() : [];
        const list = Array.isArray(data) ? data : (data?.results || []);
        const active = list.find((o) => String(o.app_status || "").toUpperCase() !== "RITIRATO") || null;
        setAppLatestOrder(active);
      } catch {
        setAppLatestOrder(null);
      }
    };

    loadLatestOrder();
    const timer = setInterval(loadLatestOrder, 20000);
    return () => clearInterval(timer);
  }, [token, headers, role]);

  useEffect(() => {
    if (!token || (role || "").toUpperCase() !== "CLIENTE") {
      setAppNotifications([]);
      return;
    }

    const loadNotifications = async () => {
      try {
        const r = await fetch(`${API_URL}/api/app/notifications/`, { headers });
        const data = r.ok ? await r.json() : [];
        setAppNotifications(Array.isArray(data) ? data : []);
      } catch {
        setAppNotifications([]);
      }
    };

    loadNotifications();
  }, [token, headers, role]);

    useEffect(() => {
      if (!token || (role || "").toUpperCase() !== "CLIENTE") return;

      (async () => {
        try {
          const r = await fetch(`${API_URL}/api/app/sponsors/visible/`, { headers });
          const data = r.ok ? await r.json() : {};
          setAppSponsors(Array.isArray(data.sponsors) ? data.sponsors : []);
          setActiveSponsorIndex(0);
        } catch {
          setAppSponsors([]);
        }
      })();
    }, [token, headers, role]);

    useEffect(() => {
      if (!appSponsors.length) return;
      const timer = setInterval(() => {
        setActiveSponsorIndex((prev) => (prev + 1) % appSponsors.length);
      }, 5000);
      return () => clearInterval(timer);
    }, [appSponsors.length]);

    const activeSponsor = appSponsors.length ? appSponsors[activeSponsorIndex % appSponsors.length] : null;


  // ---- ruolo / pacchetto
  useEffect(() => {
    if (!token) return;
    const canManageIncassi = ["MADRE", "SEDE"].includes(role);
    if (canManageIncassi) {
      setReceivedUnpaid(0);
      return;
    }

    (async () => {
      try {
        const ym = new Date().toISOString().slice(0, 7);
        const r = await fetch(`${API_URL}/api/fatture/inbox/?month=${ym}`, { headers });
        const data = r.ok ? await r.json() : {};
        const items = Array.isArray(data) ? data : (data.results || []);
        const count = items.filter((x) => !(x.is_paid || String(x.status || "").toUpperCase() === "PAID")).length;
        setReceivedUnpaid(count);
      } catch {
        setReceivedUnpaid(0);
      }
    })();
  }, [token, headers, role]);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/me/`, { headers });
        const me = await r.json();
          setMeData(me);
        setRole((me?.role || "").toUpperCase());
        setHasFullPackage(!!me?.has_full_package);
      } catch {
        setRole("");
        setHasFullPackage(false);
      }
    })();
  }, [token, headers]);

  useEffect(() => {
    if (!token || (role || "").toUpperCase() !== "CLIENTE") {
      setAppWallet(null);
      return;
    }

    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/app/wallet/`, { headers });
        const data = r.ok ? await r.json() : null;
        setAppWallet(data);
      } catch {
        setAppWallet(null);
      }
    })();
  }, [token, headers, role]);

  useEffect(() => {
    if (!token) return;

    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/fatture/?paid=0&page_size=100`, { headers });
        const data = r.ok ? await r.json() : {};
        const items = Array.isArray(data) ? data : (data.results || data.items || []);
        const count = typeof data?.count === "number" ? data.count : items.length;
        const total = items.reduce((acc, x) => acc + Number(x.total || 0), 0);
        setGlobalUnpaid({ count, total });
      } catch {
        setGlobalUnpaid({ count: 0, total: 0 });
      }
    })();
  }, [token, headers]);

  /*


  // ---- clienti attivi (solo FULL o MADRE)
  useEffect(() => {
    if (!token) return;
    if (!(hasFullPackage || isMadre)) return;

    (async () => {
      try {
        const r = await apiFetch(`${API_URL}/api/clienti/?attivi=true`, { headers });
const data = await r.json();
const arr = Array.isArray(data) ? data : Array.isArray(data?.results) ? data.results : [];
setAllClients(arr);
if (typeof data?.count === "number") setActiveClientsCount(Number(data.count || 0));
else setActiveClientsCount(null);
} catch {
        setAllClients([]);
      }
    })();
  }, [token, headers, hasFullPackage, isMadre]);

  */
    // ---- elenco store per MADRE: endpoint /api/stores/ non presente (disabilitato)

    // ---- KPI (una sola chiamata /api/kpi/)
    useEffect(() => {
      if (!token) return;

      const kpiUrl = isMadre && selectedStore
        ? `${API_URL}/api/kpi/?store=${encodeURIComponent(selectedStore)}`
        : `${API_URL}/api/kpi/`;

      (async () => {
        try {
          const rkpi = await fetch(kpiUrl, { headers });

          if (!rkpi.ok) {
            setProductsCount(0);
            setPendingOrders(0);
            setActiveClientsCount(null);
            setAllClients([]);
            return;
          }

          const data = await rkpi.json();
          setKpiData(data);

          // prodotti
          const pc =
            data?.prodotti_totali ??
            data?.products_count ??
            data?.prodotti ??
            data?.products ??
            data?.tot_prodotti ??
            0;
          setProductsCount(Number(pc || 0));

          // clienti attivi (questo è quello che ti serve per il box)
          const ac = data?.clienti_attivi;
          if (typeof ac === "number") setActiveClientsCount(Number(ac || 0));
          else if (ac != null) setActiveClientsCount(Number(ac || 0));
          else setActiveClientsCount(null);

          // non ci serve più la lista clienti per contare
          setAllClients([]);

          // ordini in attesa (APPROVED)
          const po = data?.ordini_in_attesa;
          if (typeof po === "number") setPendingOrders(Number(po || 0));
          else setPendingOrders(0);
        } catch {
          setProductsCount(0);
          setPendingOrders(0);
          setActiveClientsCount(null);
          setAllClients([]);
        }
      })();
    }, [token, headers, isMadre, selectedStore]);


  /*

  // ---- INBOX summary + elenco (in parallelo) (ruoli ≠ MADRE)
  useEffect(() => {
    if (!token) return;
    if (isMadre) return;

    (async () => {
      const summaryUrl = `${API_URL}/api/fatture/inbox/summary/?month=${inboxMonth}`;
      const listUrl = `${API_URL}/api/fatture/inbox/?month=${inboxMonth}`;

      try {
        const [rsRes, rlRes] = await Promise.allSettled([
          apiFetch(summaryUrl, { headers }),
          apiFetch(listUrl, { headers }),
        ]);

        // summary
        try {
          if (rsRes.status === "fulfilled" && rsRes.value.ok) {
            const s = await rsRes.value.json();
            setInboxEnabled(true)
            setInboxCount(Number(s?.count || 0));
            setInboxUnpaid(Number(s?.unpaid || 0));
            setInboxSum(String(s?.sum || "0.00"));
          } else {
            setInboxEnabled(false);
          }
        } catch {
          setInboxEnabled(false);
        }

        // list
        try {
          if (rlRes.status === "fulfilled" && rlRes.value.ok) {
            const l = await rlRes.value.json();
            setInboxLast(Array.isArray(l) ? l.slice(0, 10) : []);
          } else {
            setInboxLast([]);
          }
        } catch {
          setInboxLast([]);
        }
      } catch {
        setInboxEnabled(false);
        setInboxLast([]);
      }
    })();
  }, [token, headers, isMadre, inboxMonth]);
  */

  const downloadInboxPdf = (id, series, number) => {
    const name = `proforma_${series || "X"}_${number || id}.pdf`;
    downloadBlob(`${API_URL}/api/fatture/${id}/pdf/`, name, token);
  };

  const downloadInboxZip = () => {
    downloadBlob(
      `${API_URL}/api/fatture/inbox/export-zip/?month=${inboxMonth}`,
      `proforma_${inboxMonth}.zip`,
      token
    );
  };

  // ---- BOX MENU (config-driven)
  const menuBoxes = useMemo(() => {
      const boxes = [];
      const isCliente = (role || "").toUpperCase() === "CLIENTE";

      if (isCliente) {
        return [
          {
            icon: "🍷",
            title: "Catalogo prodotti",
            subtitle: "Scegli vini, alimentari e prodotti disponibili.",
            link: "/app/products",
          },
          {
            icon: "🔥",
            title: "Offerte",
            subtitle: "Scopri promozioni, novità e occasioni attive.",
            link: "/app/offers",
          },
          {
            icon: "🛒",
            title: "Carrello",
            subtitle: "Controlla il tuo ordine prima del checkout.",
            link: "/app/carrello",
          },
          {
            icon: "💳",
            title: "Wallet / Ricarica",
            subtitle: "Saldo, ricariche online e credito disponibile.",
            link: "/app/wallet",
          },
          {
            icon: "📦",
            title: "I miei ordini",
            subtitle: "Segui stato, ritiro e storico dei tuoi ordini.",
            link: "/app/ordini",
          },
          {
            icon: "🎁",
            title: "Punti e premi",
            subtitle: "Controlla punti raccolti e premi disponibili.",
            link: "/app/punti",
          },
          {
            icon: "🧪",
            title: "Centro Assistenza",
            subtitle: "Apri un ticket, invia screenshot e consulta i comunicazioni e assistenza.",
            link: "/app/beta",
          },
        ];
      }

      // Ordini
      if (!isMadre) {
        boxes.push({
          icon: "📤",
          title: "I miei ordini",
          subtitle: "Bozze e ordini inviati",
          link: "/ordini/miei",
        });
      } else {
        boxes.push({
          icon: "📥",
          title: "Ordini ricevuti (tutti)",
          subtitle: "Lista completa ordini ricevuti",
          link: "/ordini",
        });
      }

      // Clienti (solo FULL o MADRE)
      if (hasFullPackage || isMadre) {
        boxes.push({
          icon: "👥",
          title: "Clienti",
          subtitle: "Consulta punti, consumi e saldi.",
          link: "/clienti",
        });

        boxes.push({
          icon: "💰",
          title: "Wallet & Promo",
          subtitle: "Gestisci promozioni e credito app.",
          link: "/wallet",
        });
      }

      boxes.push({
        icon: "🏢",
        title: "Aziende",
        subtitle: "Visualizza e gestisci aziende.",
        link: "/aziende",
      });

      if (["MADRE", "SEDE"].includes((role || "").toUpperCase())) {
        boxes.push({
          icon: "🎯",
          title: "Promozioni B2B",
          subtitle: "Crea e gestisci campagne riservate alle aziende.",
          link: "/b2b/promozioni",
        });
      }

      boxes.push({
        icon: "🛒",
        title: "Prodotti",
        subtitle: "Consulta il catalogo prodotti.",
        link: "/prodotti",
      });

      boxes.push({
        icon: "🧑‍💼",
        title: "Agenti",
        subtitle: "Clienti, acquisti e commissioni.",
        link: "/agenti",
      });

      boxes.push({
        icon: "🏬",
        title: "Franchising",
        subtitle: "Gestione punti affiliati.",
        link: "/franchising",
      });

      boxes.push({
        icon: "🏪",
        title: "Punto Vendita",
        subtitle: "Gestione punti vendita.",
        link: "/pv",
      });

      if (isMadre) {
        boxes.push({
          icon: "📈",
          title: "Dashboard Store",
          subtitle: "Franchising / Aziende / Punto Vendita",
          link: "/dashboard-store",
        });

        boxes.push({
          icon: "🔒",
          title: "Sicurezza",
          subtitle: "Audit log accessi e password",
          link: "/security",
        });
      }

      // Fatture / proforma
      if (!isMadre) {
        boxes.push({
          icon: "🧾",
          title: "Proforma ricevute",
          subtitle: "PDF + export mensile",
          link: "/fatture/ricevute",
        });

        boxes.push({
          icon: "🗃️",
          title: "Archivio proforma",
          subtitle: "Ricevute | Emesse",
          link: "/fatture/archivio-menu",
        });
      } else {
        boxes.push({
          icon: "🧾",
          title: "Proforma",
          subtitle: "Crea, emetti e pubblica proforma",
          link: "/fatture",
        });
        boxes.push({
          icon: "🗃️",
          title: "Archivio proforma",
            subtitle: "Emesse",
            link: "/fatture/archivio?tipo=emesse",
        });

      }boxes.push({
        icon: "📣",
        title: "Offerte",
        subtitle: "Promozioni e listini",
        link: "/offers",
      });

      if (["MADRE", "SEDE", "FRANCHISING", "PV"].includes((role || "").toUpperCase())) {
      boxes.push({
        icon: "📱",
        title: "Gestione App Clienti",
        subtitle: "Wallet, offerte, media, sponsor, punti e ordini app.",
        link: "/gestione-app-clienti",
      });
    }

    return boxes;
    }, [hasFullPackage, isMadre, role]);


  const ordersTitle = isMadre ? "Ordini ricevuti (nuovi)" : "Ordini in attesa";
  const ordersLink = !role
  ? "/ordini/ricevuti?view=new"
  : isMadre
  ? "/ordini/ricevuti?view=new"
  : isFranchising || isPV
  ? "/ordini/miei?view=attesa"
  : "/ordini/miei?view=lavorazione";
  const ordersHint = isMadre ? "Apri gli ordini ricevuti in stato submitted" : "Apri i tuoi ordini in attesa";

  const isMadreUI = (meData?.role || role || "").toUpperCase() === "MADRE";

  const hasFullUI = !!(meData?.has_full_package ?? hasFullPackage) || isMadreUI;

  const activeClientsUI = kpiData?.clienti_attivi;

  const productsUI = kpiData?.prodotti_totali;

  const pendingUI = kpiData?.ordini_in_attesa;

  const [incomingSubmittedCount, setIncomingSubmittedCount] = useState(0);
  const [assistanceUnreadCount, setAssistanceUnreadCount] = useState(0);
  const [assistanceUnreadTickets, setAssistanceUnreadTickets] = useState(0);
  const [deleteRequestsPending, setDeleteRequestsPending] = useState(0);

    // KPI derivati: preferisci sempre kpiData (server), fallback agli state locali
    const clientsKpi = Number(activeClientsUI ?? activeClientsCount ?? 0);
        const pendingKpi = isMadre ? Number(incomingSubmittedCount ?? 0) : Number(pendingUI ?? pendingOrders ?? 0);




useEffect(() => {
  if (!token) return;
  if (!(isMadre || isFranchising)) return;

  (async () => {
    try {
      const r = await fetch(`${API_URL}/api/ordini/ricevuti/?status=submitted`, { headers });
      const txt = await r.text();
      let data;
      try { data = JSON.parse(txt); } catch { data = []; }

      if (!r.ok) {
        setIncomingSubmittedCount(0);
        return;
      }

      const list = Array.isArray(data) ? data : (data?.results || []);
      setIncomingSubmittedCount(list.length);
    } catch {
      setIncomingSubmittedCount(0);
    }
  })();
}, [token, headers, isMadre, isFranchising]);


  useEffect(() => {
    if (!token) return;
    if (!["MADRE", "SEDE", "FRANCHISING", "PV"].includes((role || "").toUpperCase())) return;

    let alive = true;

    const loadAssistanceUnread = async () => {
      try {
        const r = await fetch(`${API_URL}/api/admin/beta/bugs/`, { headers });
        if (!r.ok) {
          if (alive) {
            setAssistanceUnreadCount(0);
            setAssistanceUnreadTickets(0);
          }
          return;
        }

        const data = await r.json();
        const list = Array.isArray(data) ? data : [];
        const unreadTickets = list.filter((x) => Number(x.support_unread_count || 0) > 0);
        const totalUnread = unreadTickets.reduce((sum, x) => sum + Number(x.support_unread_count || 0), 0);

        if (alive) {
          setAssistanceUnreadCount(totalUnread);
          setAssistanceUnreadTickets(unreadTickets.length);
        }
      } catch {
        if (alive) {
          setAssistanceUnreadCount(0);
          setAssistanceUnreadTickets(0);
        }
      }
    };

    loadAssistanceUnread();
    const timer = setInterval(loadAssistanceUnread, 10000);

    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, [token, headers, role]);

const [incomingApprovedCount, setIncomingApprovedCount] = useState(null);

useEffect(() => {
  if (!token) return;
  if (!["MADRE", "SEDE"].includes((role || "").toUpperCase())) return;

  (async () => {
    try {
      const r = await fetch(
        `${API_URL}/api/clienti/app-delete-requests/?status=PENDING`,
        { headers }
      );

      const data = r.ok ? await r.json() : {};
      setDeleteRequestsPending(Number(data?.pending_count || 0));
    } catch {
      setDeleteRequestsPending(0);
    }
  })();
}, [token, headers, role]);

// Conteggio reale "nuovi" per MADRE/FRANCHISING: stessi dati della pagina OrdiniRicevuti
useEffect(() => {
  if (!token) return;
  if (!(isMadre || isFranchising)) return;

  (async () => {
    try {
      const r = await fetch(`${API_URL}/api/ordini/ricevuti/?status=approved`, { headers });
      if (!r.ok) {
        setIncomingApprovedCount(0);
        return;
      }
      const data = await r.json();
      const list = Array.isArray(data) ? data : (data?.results || []);
      setIncomingApprovedCount(list.length);
    } catch {
      setIncomingApprovedCount(0);
    }
  })();
}, [token, headers, isMadre, isFranchising]);




  useEffect(() => {
    if (!token || !isMadre) {
      setSecuritySummary(null);
      return;
    }

    let alive = true;

    const loadSecuritySummary = async () => {
      try {
        const r = await fetch(`${API_URL}/api/security/summary/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await r.json().catch(() => null);
        if (alive && r.ok) setSecuritySummary(data);
      } catch {
        if (alive) setSecuritySummary(null);
      }
    };

    loadSecuritySummary();
    const intervalId = setInterval(loadSecuritySummary, 60000);

    return () => {
      alive = false;
      clearInterval(intervalId);
    };
  }, [token, isMadre]);

  return (
    <div className="container py-4">

      
      {/* HEADER */}
      {(role || "").toUpperCase() !== "CLIENTE" && (
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3 p-3 border rounded-4 shadow-sm bg-white">
        <div className="d-flex align-items-center gap-3">
          <img
            src={logo}
            alt="DZR"
            style={{ width: 90, height: 90, borderRadius: 12, objectFit: "contain", filter: "drop-shadow(0 8px 16px rgba(0,0,0,0.18))" }}
          />
          <div>
            <h3 className="mb-0">Gestionale Dallo Zio Roby</h3>
            <div className="text-muted small">
              {isMadre ? "SEDE" : role || "Utente"} {isMadre && selectedStore ? `• KPI store: ${selectedStore}` : ""}
              </div>
          </div>
        </div>

        <div className="d-flex align-items-center gap-2">
            {isMadre && (
              <button
                className="btn btn-outline-danger btn-sm position-relative"
                onClick={() => nav("/security")}
                title="Apri centro sicurezza"
              >
                🔔 Sicurezza
                {(Number(securitySummary?.blocked_count || 0) + Number(securitySummary?.recent_high || 0)) > 0 && (
                  <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                  {Number(securitySummary.blocked_count || 0) + Number(securitySummary.recent_high || 0)}
                </span>
                )}
              </button>
            )}
          <button className="btn btn-outline-secondary btn-sm" onClick={() => nav("/dashboard")}>
            Home
          </button>
          <button className="btn btn-outline-danger btn-sm" onClick={handleLogout}>
            Logout
          </button>
        </div>
      </div>

      )}

      {/* USER CARD (manteniamo UserInfo, ma lo rendiamo più “compatto” visivamente) */}
      {(role || "").toUpperCase() !== "CLIENTE" && <UserInfo />}

        {assistanceUnreadCount > 0 && (
          <div className="alert alert-danger rounded-4 shadow-sm d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
            <div>
              <div className="fw-bold">💬 Centro Assistenza</div>
              <div>
                Hai {assistanceUnreadCount} messagg{assistanceUnreadCount === 1 ? "io" : "i"} da leggere
                {assistanceUnreadTickets > 0 ? ` in ${assistanceUnreadTickets} ticket` : ""}.
              </div>
            </div>
            <button
              type="button"
              className="btn btn-danger rounded-pill px-4 fw-semibold"
              onClick={() => nav("/gestione-app-clienti/assistenza")}
            >
              Apri Centro Assistenza
            </button>
          </div>
        )}

      {/* STORE SELECTOR (solo MADRE) */}
      {isMadre && (
        <div className="card shadow-sm mb-3">
          <div className="card-body d-flex flex-wrap align-items-center gap-2">
            <div className="fw-bold">Vedi KPI per store:</div>
            {stores.length > 0 ? (
                <select
                  className="form-select form-select-sm"
                  style={{ width: 260 }}
                  value={selectedStore}
                  onChange={(e) => setSelectedStore(e.target.value)}
                >
                  <option value="">(tutti gli store)</option>
                  {stores.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  className="form-control form-control-sm"
                  style={{ width: 260 }}
                  placeholder="Es. ZioRoby (opzionale)"
                  value={selectedStore}
                  onChange={(e) => setSelectedStore(e.target.value)}
                />
              )}
              <div className="text-muted small ms-auto">
                Suggerimento: lascia vuoto per KPI globali.
              </div>
            </div>
          </div>
        )}

        {(isMadreUI || (role || "").toUpperCase() === "SEDE") && deleteRequestsPending > 0 && (
          <div
            className="card shadow-sm border-danger mb-3"
            role="button"
            onClick={() => nav("/gestione-app-clienti/eliminazione-account")}
            style={{ cursor: "pointer" }}
            title="Apri richieste eliminazione account"
          >
            <div className="card-body d-flex justify-content-between align-items-center gap-3">
              <div>
                <div className="text-danger fw-bold">
                  Richieste eliminazione account
                </div>

                <div className="d-flex align-items-center gap-2 mt-1">
                  <div className="kpi-number kpi-pop">
                    {deleteRequestsPending}
                  </div>

                  <span
                    className="badge bg-danger"
                    style={{ whiteSpace: "nowrap" }}
                  >
                    Da gestire
                  </span>
                </div>

                <div className="small text-muted mt-1">
                  Clicca qui per visualizzare le richieste in attesa.
                </div>
              </div>

              <div
                className="fw-bold text-danger"
                style={{ fontSize: 18 }}
              >
                DEL
              </div>
            </div>
          </div>
        )}

        {/* KPI ROW */}
      <div className={`row g-3 ${(role || "").toUpperCase() === "CLIENTE" ? "d-none" : ""}`}>
        <div className="col-12 col-md-4">
          <div className="card shadow-sm">
            <div className="card-body d-flex justify-content-between align-items-center">
              <div>
                <div className="kpi-label">Clienti attivi</div>
                <div className="kpi-number kpi-pop" key={`kpi-clients-${hasFullPackage || isMadre ? activeClients : "x"}`}>{(hasFullPackage || (role||"").toUpperCase()==="MADRE") ? (kpiData?.clienti_attivi ?? "—") : "—"}</div>
                {!hasFullUI && !isMadreUI && (
                  <div className="small text-muted">{!isMadre ? "Disponibile con pacchetto FULL" : ""}</div>
                )}
              </div>
              <div style={{ fontSize: 38 }}>👥</div>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-4">
          <div className="card shadow-sm" role="button" title="Apri prodotti" onClick={() => nav("/prodotti")}>
            <div className="card-body d-flex justify-content-between align-items-center">
              <div>
                <div className="kpi-label">Prodotti ✅</div>
                <div className="kpi-number kpi-pop" key={`kpi-products-${Number(kpiData?.prodotti_totali ?? 0)}`}>{Number(kpiData?.prodotti_totali ?? 0)}</div>
              </div>
              <div style={{ fontSize: 38 }}>🛒</div>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-4">
          <div className="card shadow-sm" role="button" onClick={() => nav(ordersLink)} title={ordersHint}>
            <div className="card-body d-flex justify-content-between align-items-center">
              <div>
                <div className="kpi-label">{ordersTitle}</div>
                  {(() => {
                      const ordiniInAttesaUI = Number(
                        isMadreUI ? (incomingSubmittedCount ?? 0) : (kpiData?.ordini_in_attesa || 0)
                      );
                      return (
                        <>
                          <div className="kpi-number d-flex align-items-center gap-2 kpi-pop" key={`kpi-orders-${ordiniInAttesaUI}`}>
                            {ordiniInAttesaUI}
                            {ordiniInAttesaUI > 0 && (
                              <span className="badge bg-danger" style={{ fontSize: 12, padding: "6px 10px" }}>
                                Nuovi
                              </span>
                )}
                          </div>
                          {ordiniInAttesaUI > 0 && (
                            <div className="small text-danger mt-1">
                              {isMadreUI ? "Ordini in arrivo da evadere" : "Ordini in attesa"}
                            </div>
                          )}
                        </>
                      );
                    })()}
                </div>
            </div>
          </div>
        </div>
      </div>


        {(isMadreUI || isFranchising || isPV) && (
          <div className={`row g-3 mt-1 ${(role || "").toUpperCase() === "CLIENTE" ? "d-none" : ""}`}>
            <div className="col-12 col-md-6">
              <div
                className="card shadow-sm border-primary h-100"
                role="button"
                onClick={() => nav("/ordini/ricevuti?source=app&app_view=active&status=submitted")}
                title="Ordini arrivati dall’app clienti"
              >
                <div className="card-body d-flex justify-content-between align-items-center">
                  <div>
                    <div className="text-muted small">App clienti</div>
                    <div className="fw-bold fs-5">Ordini App Clienti →</div>
                    <div className="small text-muted">Da preparare, pronti o da ritirare</div>
                  </div>
                  <div className="d-flex align-items-center gap-3">
                    <div className="text-end">
                      <div className="kpi-number kpi-pop">{Number(kpiData?.ordini_app_nuovi ?? 0)}</div>
                      {Number(kpiData?.ordini_app_nuovi ?? 0) > 0 && (
                        <span className="badge bg-danger">Nuovi</span>
                      )}
                    </div>
                    <div style={{ fontSize: 38 }}>📱</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-md-6">
              <div
                className="card shadow-sm h-100"
                role="button"
                onClick={() => nav("/ordini/ricevuti?source=app&app_view=history&status=all")}
                title="Storico ordini app clienti ritirati"
              >
                <div className="card-body d-flex justify-content-between align-items-center">
                  <div>
                    <div className="text-muted small">App clienti</div>
                    <div className="fw-bold fs-5">Storico Ordini App Clienti →</div>
                    <div className="small text-muted">Ordini ritirati, archivio e statistiche</div>
                  </div>
                  <div style={{ fontSize: 38 }}>📚</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* QUICK ACTIONS ORDINI */}
          {!isAziendaUI && (
        <div className={`row g-3 mt-1 ${(role || "").toUpperCase() === "CLIENTE" ? "d-none" : ""}`}>
          {(isFranchising || isPV) ? (
            <>
              <div className="col-12 col-md-4">
                <div
                  className="card shadow-sm"
                  role="button"
                  onClick={() => nav("/ordini/ricevuti?view=new")}
                  title="Ordini ricevuti dalle aziende/clienti collegati"
                >
                  <div className="card-body d-flex justify-content-between align-items-center">
                    <div>
                      <div className="text-muted small">Ordini ricevuti</div>
                      <div className="fw-bold">Nuovi da clienti →</div>
                    </div>
                    <div style={{ fontSize: 34 }}>📦</div>
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-4">
                <div
                  className="card shadow-sm"
                  role="button"
                  onClick={() => nav("/ordini/miei?view=lavorazione")}
                  title="Apri gli ordini inviati alla Sede ancora in lavorazione"
                >
                  <div className="card-body d-flex justify-content-between align-items-center">
                    <div>
                      <div className="text-muted small">Ordini inviati alla Sede</div>
                      <div className="fw-bold">In lavorazione →</div>
                    </div>
                    <div style={{ fontSize: 34 }}>🧾</div>
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-4">
                <div
                  className="card shadow-sm"
                  role="button"
                  onClick={() => nav("/ordini/storico")}
                  title="Storico ordini inviati e ricevuti"
                >
                  <div className="card-body d-flex justify-content-between align-items-center">
                    <div>
                      <div className="text-muted small">Storico ordini</div>
                      <div className="fw-bold">Inviati e ricevuti →</div>
                    </div>
                    <div style={{ fontSize: 34 }}>📥</div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="col-12 col-md-6">
                <div
                  className="card shadow-sm"
                  role="button"
                  onClick={() => nav(ordersLink)}
                  title={isMadre ? "Apri solo ordini ricevuti nuovi da gestire" : "Apri i tuoi ordini in lavorazione"}
                >
                  <div className="card-body d-flex justify-content-between align-items-center">
                    <div>
                      <div className="text-muted small">Ordini</div>
                      <div className="fw-bold">Nuovi / in attesa →</div>
                    </div>
                    <div style={{ fontSize: 34 }}>🧾</div>
                  </div>
                </div>
              </div>

              <div className="col-12 col-md-6">
                <div
                  className="card shadow-sm"
                  role="button"
                  onClick={() => nav(role ? (isMadre ? "/ordini" : "/ordini/miei") : "/ordini")}
                  title="Apri l’elenco completo"
                >
                  <div className="card-body d-flex justify-content-between align-items-center">
                    <div>
                      <div className="text-muted small">Ordini</div>
                      <div className="fw-bold">Tutti →</div>
                    </div>
                    <div style={{ fontSize: 34 }}>📥</div>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

          )}
      {/* INBOX PROFORMA */}
      {/* INBOX PROFORMA TEMP DISABLED */}

          {(role || "").toUpperCase() === "CLIENTE" && (
            <div className="mb-4" style={{ marginTop: -8 }}>
              <div
                className="rounded-5 shadow-lg overflow-hidden"
                style={{
                  background: "radial-gradient(circle at 20% 0%, #8b1e2d 0%, #5b0f1b 45%, #211014 100%)",
                  color: "#fff",
                  border: "1px solid rgba(255,255,255,0.12)"
                }}
              >
                <div className="p-4 pb-3 text-center">
                  <img
                    src={logo}
                    alt="Dallo Zio Roby"
                    style={{
                      width: 118,
                      height: 118,
                      objectFit: "contain",
                      filter: "drop-shadow(0 14px 26px rgba(0,0,0,0.35))"
                    }}
                  />
                  <div className="mt-2 small text-uppercase" style={{ letterSpacing: 1.4, opacity: 0.75 }}>
                    App Clienti
                  </div>
                  <h1 className="fw-bold mb-1" style={{ fontSize: 30 }}>
                    Dallo Zio Roby
                  </h1>
                  <div className="mx-auto" style={{ maxWidth: 320, opacity: 0.84 }}>
                    Ordina il tuo vino, ricarica il wallet e segui punti e promozioni.
                  </div>
                </div>

                <div className="px-3 pb-4">
                  {appWallet && (
                    <div
                      className="rounded-4 p-3 mb-3 d-flex justify-content-between align-items-center"
                      style={{ background: "rgba(255,255,255,0.12)", backdropFilter: "blur(8px)" }}
                    >
                      <div>
                        <div className="small" style={{ opacity: 0.75 }}>Credito disponibile</div>
                        <div className="fw-bold" style={{ fontSize: 30 }}>
                          € {Number(appWallet.total_balance || 0).toFixed(2)}
                        </div>
                      </div>
                      <div style={{ fontSize: 34 }}>💳</div>
                    </div>
                  )}

                    {appNotifications.length > 0 && (
                      <div
                        className="rounded-4 p-3 mb-3 d-flex justify-content-between align-items-center"
                        role="button"
                        onClick={() => nav("/app/notifications")}
                        style={{ background: "rgba(255,255,255,0.18)", backdropFilter: "blur(8px)", cursor: "pointer" }}
                      >
                        <div>
                          <div className="small" style={{ opacity: 0.75 }}>Notifiche</div>
                          <div className="fw-bold" style={{ fontSize: 18 }}>
                            {appNotifications.filter((n) => !n.is_read).length > 0
                              ? `${appNotifications.filter((n) => !n.is_read).length} notifica da leggere`
                              : "Nessuna nuova notifica"}
                          </div>
                          <div className="small" style={{ opacity: 0.75 }}>Tocca per aprire le notifiche →</div>
                        </div>
                        <div style={{ fontSize: 34 }}>🔔</div>
                      </div>
                    )}

                  <div className="d-grid gap-2">
                    <button
                      type="button"
                      className="btn btn-light btn-lg rounded-pill fw-bold py-3"
                      onClick={() => nav("/app/products")}
                    >
                      🍷 Ordina ora
                    </button>

                      <button
                        type="button"
                        className="btn btn-outline-light rounded-pill fw-bold py-3"
                        onClick={() => nav("/app/wallet")}
                      >
                        💳 Ricarica wallet
                      </button>

                      <a
                        href="https://gestionale.dallozioroby.it/elimina-account"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-outline-light rounded-pill py-3 text-start"
                        style={{ whiteSpace: "normal" }}
                      >
                        <div className="small opacity-75">Account</div>
                        <div className="fw-bold">⚙️ Gestione account / eliminazione</div>
                      </a>

                  </div>
                </div>
              </div>
            </div>
          )}



            {(role || "").toUpperCase() === "CLIENTE" && appLatestOrder && (() => {
            const status = String(appLatestOrder.app_status || appLatestOrder.status || "").toUpperCase();
            const step =
              status === "IN_PREPARAZIONE" ? 2 :
              status === "PRONTO_RITIRO" ? 3 :
              status === "RITIRATO" ? 4 :
              1;
            const label =
              step === 2 ? "In preparazione" :
              step === 3 ? "Pronto al ritiro" :
              step === 4 ? "Ritirato" :
              "Ordine ricevuto";

            return (
              <div
                className="card border-0 rounded-5 shadow-sm mb-3 overflow-hidden"
                role="button"
                onClick={() => nav("/app/ordini")}
                style={{ background: "#fff8f1", cursor: "pointer", border: "1px solid rgba(91,15,27,0.10)" }}
              >
                <div className="card-body p-3">
                  <div className="d-flex justify-content-between align-items-center gap-3">
                    <div>
                      <div className="small text-muted">Ordine in corso</div>
                      <div className="fw-bold" style={{ color: "#5b0f1b", fontSize: 18 }}>
                        #{appLatestOrder.id}
                      </div>
                    </div>
                    <div style={{ fontSize: 30 }}>📦</div>
                  </div>

                  <div className="d-flex align-items-center gap-3 mt-3">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{
                        width: 44,
                        height: 44,
                        background: "#5b0f1b",
                        color: "#fff",
                        fontWeight: 900,
                        fontSize: 20
                      }}
                    >
                      {step}
                    </div>

                    <div>
                      <div className="fw-bold" style={{ color: "#5b0f1b", fontSize: 18 }}>{label}</div>
                      <div className="small text-muted">Tocca per vedere tutti i tuoi ordini →</div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })()}

        {/* MENU BOXES */}
        <div className="row mt-4">
          {menuBoxes.map((b) => (
            <HomeBox
              key={`${b.title}-${b.link}`}
              icon={b.icon}
              title={b.title}
              subtitle={b.subtitle}
              link={b.link}
                unpaidCount={(b.link === "/fatture/archivio" && (isMadre || role === "SEDE")) ? globalUnpaid.count : 0}
                receivedUnpaidCount={b.link === "/fatture/ricevute" ? receivedUnpaid : 0}
            />
          ))}
        </div>

        {(role || "").toUpperCase() === "CLIENTE" && (
          <div className="card border-0 shadow-sm rounded-4 mb-4 overflow-hidden">
            <div
              className="card-body text-center p-4"
              style={{
                background: "linear-gradient(135deg, #1f1f1f 0%, #2f2f2f 100%)",
                color: "#fff"
              }}
            >
              <div className="small text-uppercase opacity-75 mb-2">
                Sponsor Partner
              </div>

              {activeSponsor ? (
                <div
                  role={activeSponsor.website_url ? "button" : undefined}
                  onClick={() => {
                    if (!activeSponsor.website_url) return;
                    const url = activeSponsor.website_url.startsWith("http")
                      ? activeSponsor.website_url
                      : `https://${activeSponsor.website_url}`;
                    window.open(url, "_blank", "noopener,noreferrer");
                  }}
                  style={{ cursor: activeSponsor.website_url ? "pointer" : "default" }}
                >
                  {activeSponsor.image && (
                    <img
                      src={activeSponsor.image}
                      alt={activeSponsor.name}
                      className="img-fluid rounded-4 bg-white p-2 mb-3"
                      style={{ maxHeight: 130, objectFit: "contain" }}
                    />
                  )}

                  <div className="fw-bold fs-4 mb-2">{activeSponsor.name}</div>

                  <div className="opacity-75 mb-3">
                    {activeSponsor.description || "Partner ufficiale Dallo Zio Roby."}
                  </div>

                  <button type="button" className="btn btn-outline-light rounded-pill px-4">
                    {activeSponsor.button_label || "Visita"}
                  </button>

                  {appSponsors.length > 1 && (
                    <div className="small opacity-75 mt-3">
                      {activeSponsorIndex + 1} / {appSponsors.length}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div className="fw-bold fs-4 mb-2">Qui potrà comparire il tuo brand</div>
                  <div className="opacity-75 mb-3">
                    Spazio dedicato ai partner e sponsor ufficiali Dallo Zio Roby.
                  </div>
                  <button type="button" className="btn btn-outline-light rounded-pill px-4">
                    Diventa Sponsor
                  </button>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
}

